/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'

import { EuiFormHelpText, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiAlert } from '@modules/cui/Alert'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'
import { DocLink } from '@modules/doclinks'

import SpinButton from '../../SpinButton'
import FormGroup from '../../FormGroup'

import type { Props } from './types'

class RetryFailedShards extends Component<Props> {
  componentWillUnmount(): void {
    this.props.resetRetryFailedShardAllocationsRequest()
  }

  render(): JSX.Element {
    const {
      canRetryFailedShards,
      retryFailedShardAllocations,
      retryFailedShardAllocationsRequest,
      deployment,
    } = this.props

    const successfulRequest =
      !retryFailedShardAllocationsRequest.error && retryFailedShardAllocationsRequest.isDone

    return (
      <FormGroup
        label={<FormattedMessage id='retry-failed-shards.title' defaultMessage='Failed Shards' />}
      >
        <EuiSpacer size='s' />

        <PermissionsGate
          permissions={[
            {
              type: 'deployment',
              action: 'get',
              id: deployment.id,
            },
          ]}
        >
          {({ hasPermissions }) => (
            <SpinButton
              color='primary'
              size='s'
              onClick={() => retryFailedShardAllocations()}
              disabled={
                retryFailedShardAllocationsRequest.isDone ||
                !canRetryFailedShards ||
                !hasPermissions
              }
              spin={retryFailedShardAllocationsRequest.inProgress}
            >
              <FormattedMessage id='retry-failed-shards.retry' defaultMessage='Retry' />
            </SpinButton>
          )}
        </PermissionsGate>

        {this.renderHelpText()}

        {retryFailedShardAllocationsRequest.error && (
          <Fragment>
            <EuiSpacer size='s' />
            <CuiAlert type='error'>{retryFailedShardAllocationsRequest.error}</CuiAlert>
          </Fragment>
        )}

        {successfulRequest && (
          <Fragment>
            <EuiSpacer size='s' />
            <CuiAlert type='info'>
              <FormattedMessage
                id='retry-failed-shards.success'
                defaultMessage='Shard reallocation has started. You can view the progress by connecting to your cluster and looking at the cat shards API.'
              />
            </CuiAlert>
          </Fragment>
        )}
      </FormGroup>
    )
  }

  renderHelpText(): JSX.Element {
    const { canRetryFailedShards } = this.props

    if (canRetryFailedShards) {
      return (
        <EuiFormHelpText>
          <FormattedMessage
            id='retry-failed-shards.description'
            defaultMessage='If you have shards that failed to allocate, you can try to allocate them again. {learnMore}...'
            values={{
              learnMore: (
                <DocLink link='esFailedShards'>
                  <FormattedMessage
                    id='retry-failed-shards.learn-more'
                    defaultMessage='Learn more'
                  />
                </DocLink>
              ),
            }}
          />
        </EuiFormHelpText>
      )
    }

    return (
      <EuiFormHelpText>
        <FormattedMessage
          id='retry-failed-shards.description-old-version'
          defaultMessage='Only available starting with v5.0.0'
        />
      </EuiFormHelpText>
    )
  }
}

export default RetryFailedShards
